/**
 * Shared country → currency/tax/locale configuration.
 *
 * This is the single source of truth for:
 *   - which currency a country uses
 *   - the default tax name and rate for that country
 *   - the Intl locale for number/currency formatting
 *
 * Consumed by TaxSettingsControl, format.utils, proposal-snapshot-transformer, etc.
 */

export interface CountryConfig {
  /** ISO 3166-1 alpha-2 country code */
  code: string
  /** Human-readable country name */
  name: string
  /** Emoji flag */
  flag: string
  /** Local tax name (e.g. 'GST', 'VAT') */
  taxName: string
  /** Tax rate as a decimal (0.1 = 10%) */
  taxRate: number
  /** ISO 4217 currency code */
  currencyCode: string
  /** BCP 47 locale tag for Intl formatting */
  locale: string
}

export const DEFAULT_COUNTRY_CODE = 'AU'

export const COUNTRY_CONFIGS: readonly CountryConfig[] = [
  {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    taxName: 'GST',
    taxRate: 0.1,
    currencyCode: 'AUD',
    locale: 'en-AU',
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿',
    taxName: 'GST',
    taxRate: 0.15,
    currencyCode: 'NZD',
    locale: 'en-NZ',
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    taxName: 'VAT',
    taxRate: 0.2,
    currencyCode: 'GBP',
    locale: 'en-GB',
  },
  {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    taxName: 'Sales Tax',
    taxRate: 0.07,
    currencyCode: 'USD',
    locale: 'en-US',
  },
  {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    taxName: 'GST/HST',
    taxRate: 0.05,
    currencyCode: 'CAD',
    locale: 'en-CA',
  },
  {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪',
    taxName: 'MwSt',
    taxRate: 0.19,
    currencyCode: 'EUR',
    locale: 'de-DE',
  },
  {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷',
    taxName: 'TVA',
    taxRate: 0.2,
    currencyCode: 'EUR',
    locale: 'fr-FR',
  },
  {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹',
    taxName: 'IVA',
    taxRate: 0.22,
    currencyCode: 'EUR',
    locale: 'it-IT',
  },
  {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸',
    taxName: 'IVA',
    taxRate: 0.21,
    currencyCode: 'EUR',
    locale: 'es-ES',
  },
  {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱',
    taxName: 'BTW',
    taxRate: 0.21,
    currencyCode: 'EUR',
    locale: 'nl-NL',
  },
  {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪',
    taxName: 'Moms',
    taxRate: 0.25,
    currencyCode: 'SEK',
    locale: 'sv-SE',
  },
  {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰',
    taxName: 'Moms',
    taxRate: 0.25,
    currencyCode: 'DKK',
    locale: 'da-DK',
  },
  {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴',
    taxName: 'MVA',
    taxRate: 0.25,
    currencyCode: 'NOK',
    locale: 'nb-NO',
  },
  {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭',
    taxName: 'MWST',
    taxRate: 0.077,
    currencyCode: 'CHF',
    locale: 'de-CH',
  },
  {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵',
    taxName: 'Consumption Tax',
    taxRate: 0.1,
    currencyCode: 'JPY',
    locale: 'ja-JP',
  },
  {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    taxName: 'GST',
    taxRate: 0.09,
    currencyCode: 'SGD',
    locale: 'en-SG',
  },
  {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    taxName: 'GST',
    taxRate: 0.18,
    currencyCode: 'INR',
    locale: 'en-IN',
  },
  {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷',
    taxName: 'ICMS',
    taxRate: 0.17,
    currencyCode: 'BRL',
    locale: 'pt-BR',
  },
  {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽',
    taxName: 'IVA',
    taxRate: 0.16,
    currencyCode: 'MXN',
    locale: 'es-MX',
  },
  {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦',
    taxName: 'VAT',
    taxRate: 0.15,
    currencyCode: 'ZAR',
    locale: 'en-ZA',
  },
] as const

/** Pre-built lookup map for O(1) access by country code. */
const configByCode = new Map<string, CountryConfig>(COUNTRY_CONFIGS.map(c => [c.code, c]))

const DEFAULT_CONFIG = configByCode.get(DEFAULT_COUNTRY_CODE)!

/** Get the full config for a country code, falling back to Australia. */
export function getCountryConfig(countryCode: string): CountryConfig {
  return configByCode.get(countryCode) ?? DEFAULT_CONFIG
}

/** Get the ISO 4217 currency code for a country code. */
export function getCurrencyForCountry(countryCode: string): string {
  return getCountryConfig(countryCode).currencyCode
}

/** Get the BCP 47 locale tag for a country code. */
export function getLocaleForCountry(countryCode: string): string {
  return getCountryConfig(countryCode).locale
}
